import { useState, useEffect, useRef } from "react";
import { useDocPrice, priceLabel } from "@/utils/docPrices";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { FileText, MapPin, Landmark, Wallet, Users, ShieldCheck, Calculator, Download } from "lucide-react";
import { STATE_DATA, STATE_SLUGS } from "@/data/stateData";

// Same reveal hook the marketing pages use (threshold 0.05 per DESIGN.md)
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const fade = (inView) =>
  `transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`;

export default function StatePaystubLanding() {
  const { state } = useParams();
  const navigate = useNavigate();
  // the pay stub price as the admin set it (Preferences > Document prices)
  const P = priceLabel(useDocPrice("paystub", 9.99));
  const info = STATE_DATA[state];

  const [heroRef, heroInView] = useInView();
  const [factsRef, factsInView] = useInView();
  const [builtRef, builtInView] = useInView();
  const [howRef, howInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [statesRef, statesInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // Scroll restoration between state pages (Link navigations keep position)
  useEffect(() => { window.scrollTo(0, 0); }, [state]);

  if (!info) return <Navigate to="/paystub-generator" replace />;

  const isCanada = state === "canada";
  const ctaPath = isCanada ? "/app/canadian-paystub" : "/app/paystubs";
  const regionWord = isCanada ? "province" : "state";

  const faqs = [
    {
      q: `Does ${info.name} have ${isCanada ? "income tax withholding" : "state income tax"}?`,
      a: info.hasStateTax
        ? `Yes. ${info.name} ${isCanada ? "withholds federal and provincial income tax" : `has a state income tax of ${info.taxRate}`}. MintSlip calculates the correct withholdings automatically along with ${isCanada ? "CPP and EI" : "federal tax, Social Security, and Medicare"}.`
        : `No. ${info.name} has no state income tax, so only federal taxes (federal income tax, Social Security, and Medicare) are withheld. MintSlip handles this automatically when you select ${info.name}.`,
    },
    {
      q: `What should a ${info.name} pay stub include?`,
      a: `${info.compliance} A complete stub shows gross pay, itemized deductions, net pay, and year-to-date totals. MintSlip includes all of them on every template.`,
    },
    {
      q: `How much does a ${info.name} paystub cost?`,
      a: `Each paystub is ${P} with instant PDF download and a copy sent to your email. No subscription and no sign-up required.`,
    },
    {
      q: `Can I make a paystub for ${info.majorCities[0]}?`,
      a: `Yes, MintSlip works for any employer or employee anywhere in ${info.name}, including ${info.majorCities.slice(0, 3).join(", ")}. The ${regionWord} selection sets the right tax withholdings; everything else is filled in by you.`,
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const appSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": `${info.name} Paystub Generator`,
    "description": `Professional paystub generator for ${info.name} with automatic tax calculations`,
    "url": `https://mintslip.com/paystub-generator/${state}`,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": { "@type": "Offer", "price": "9.99", "priceCurrency": "USD" },
    "areaServed": { "@type": isCanada ? "Country" : "State", "name": info.name },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://mintslip.com" },
      { "@type": "ListItem", "position": 2, "name": "Paystub Generator", "item": "https://mintslip.com/paystub-generator" },
      { "@type": "ListItem", "position": 3, "name": info.name, "item": `https://mintslip.com/paystub-generator/${state}` },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{`${info.name} Paystub Generator: Instant ${info.abbr} Pay Stubs | MintSlip`}</title>
        <meta name="description" content={`Create professional ${info.name} pay stubs in minutes with accurate ${info.hasStateTax ? `${info.taxRate} ${isCanada ? "" : "state "}tax` : "no-state-tax"} withholdings. Instant PDF download, no sign-up required.`} />
        <link rel="canonical" href={`https://mintslip.com/paystub-generator/${state}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://mintslip.com/paystub-generator/${state}`} />
        <meta property="og:title" content={`${info.name} Paystub Generator | MintSlip`} />
        <meta property="og:description" content={`Create professional ${info.name} pay stubs in minutes with automatic tax calculations.`} />
        <meta property="og:image" content="https://mintslip.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${info.name} Paystub Generator | MintSlip`} />
        <meta name="twitter:description" content={`Create professional ${info.name} pay stubs in minutes with automatic tax calculations.`} />
        <meta name="twitter:image" content="https://mintslip.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Header title="MintSlip" />

      {/* Hero */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-10 md:pt-20 md:pb-14">
        <div className={`text-center ${fade(heroInView)}`}>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 rounded-full border border-emerald-200/80 text-sm font-medium text-emerald-900 mb-6">
            <MapPin className="w-4 h-4 text-emerald-700" />
            {info.abbr} &middot; Paystub Generator
          </div>
          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Create a <span className="font-black">{info.name}</span> paystub in minutes
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Professional pay stubs with accurate {info.name} withholdings, calculated automatically.
            Trusted across {info.majorCities.slice(0, 3).join(", ")}, and all of {info.name}.
          </p>
          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate(ctaPath)}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
          <p className="text-sm text-slate-500">Instant download. No sign-up required.</p>
        </div>
      </section>

      {/* State facts bento */}
      <section ref={factsRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-5 ${fade(factsInView)}`}>
          <div className="rounded-[28px] bg-emerald-400 p-6 md:p-7">
            <span className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center mb-4">
              <Landmark className="w-5 h-5 text-emerald-700" />
            </span>
            <div className="font-display text-2xl md:text-3xl font-black tracking-tight text-emerald-950">
              {info.hasStateTax ? info.taxRate : "0%"}
            </div>
            <div className="text-sm font-medium text-emerald-900/80 mt-1">
              {isCanada ? "Income tax" : info.hasStateTax ? "State income tax" : "No state income tax"}
            </div>
          </div>
          <div className="rounded-[28px] bg-slate-100 p-6 md:p-7">
            <span className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center mb-4">
              <Wallet className="w-5 h-5 text-slate-900" />
            </span>
            <div className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-900">{info.minWage}</div>
            <div className="text-sm font-medium text-slate-600 mt-1">Minimum wage</div>
          </div>
          <div className="rounded-[28px] bg-[#0b0b0b] p-6 md:p-7">
            <span className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center mb-4">
              <Users className="w-5 h-5 text-emerald-400" />
            </span>
            <div className="font-display text-2xl md:text-3xl font-black tracking-tight text-white">{info.population}</div>
            <div className="text-sm font-medium text-slate-400 mt-1">People in {info.name}</div>
          </div>
          <div className="rounded-[28px] bg-slate-100 p-6 md:p-7">
            <span className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center mb-4">
              <MapPin className="w-5 h-5 text-slate-900" />
            </span>
            <div className="font-display text-lg md:text-xl font-black tracking-tight text-slate-900 leading-snug">
              {info.majorCities.slice(0, 3).join(" · ")}
            </div>
            <div className="text-sm font-medium text-slate-600 mt-1">And every other city</div>
          </div>
        </div>
      </section>

      {/* Built for the state */}
      <section ref={builtRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
        <div className={fade(builtInView)}>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-8">
            Built for <span className="font-black">{info.name}</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="rounded-[28px] bg-slate-100 p-7 md:p-9">
              <span className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                <ShieldCheck className="w-6 h-6 text-slate-900" />
              </span>
              <h3 className="font-display text-xl font-bold tracking-tight text-slate-900 mb-2.5">
                {info.name} pay stub requirements
              </h3>
              <p className="text-slate-600 leading-relaxed">{info.compliance}</p>
              <p className="text-xs text-slate-500 mt-4">
                General information, not legal advice. Verify current requirements with your {regionWord} labor department.
              </p>
            </div>
            <div className="grid grid-rows-2 gap-5">
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-7 flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-emerald-50 border border-emerald-200/80 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-5 h-5 text-emerald-700" />
                </span>
                <div>
                  <h3 className="font-display font-bold tracking-tight text-slate-900 mb-1">Taxes done for you</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {info.hasStateTax
                      ? `${isCanada ? "Federal and provincial income tax, CPP, and EI" : `Federal income tax, ${info.name} state tax (${info.taxRate}), Social Security, and Medicare`} are calculated automatically from your pay details.`
                      : `Federal income tax, Social Security, and Medicare are calculated automatically, and because ${info.name} has no state income tax, nothing extra is withheld.`}
                  </p>
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-7 flex items-start gap-4">
                <span className="w-11 h-11 rounded-full bg-emerald-50 border border-emerald-200/80 flex items-center justify-center flex-shrink-0">
                  <Download className="w-5 h-5 text-emerald-700" />
                </span>
                <div>
                  <h3 className="font-display font-bold tracking-tight text-slate-900 mb-1">Instant, professional PDFs</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Pick a template, preview your stub live, and download the finished PDF the moment you pay, with accurate year-to-date totals on every stub.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section ref={howRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
        <div className={fade(howInView)}>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-8">
            Three steps. <span className="font-black">Done.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { n: 1, t: "Enter your details", d: `Company, employee, and pay info. Select ${info.name} and the ${isCanada ? "provincial" : "state"} taxes are set automatically.` },
              { n: 2, t: "Preview instantly", d: "See your finished paystub update live as you type, with every calculation done for you." },
              { n: 3, t: P === "free" ? "Download free" : `Download for ${P}`, d: P === "free" ? "Your PDF downloads instantly, plus a copy lands in your email." : "Pay securely and your PDF downloads instantly, plus a copy lands in your email." },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-7">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-400 font-display font-black text-emerald-950 mb-4">
                  {s.n}
                </span>
                <h3 className="font-display font-bold tracking-tight text-slate-900 mb-1.5">{s.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
        <div className={fade(faqInView)}>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-8">
            {info.name} paystub <span className="font-black">questions</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {faqs.map((f) => (
              <div key={f.q} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-7">
                <h3 className="font-display font-bold tracking-tight text-slate-900 mb-2">{f.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other states — internal linking mesh */}
      {!isCanada && (
        <section ref={statesRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
          <div className={fade(statesInView)}>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-8">
              Every other <span className="font-black">state</span>
            </h2>
            <div className="flex flex-wrap gap-2">
              {STATE_SLUGS.filter((s) => s !== state).map((s) => (
                <Link
                  key={s}
                  to={`/paystub-generator/${s}`}
                  className="px-3.5 py-1.5 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                >
                  {STATE_DATA[s].name}
                </Link>
              ))}
              <Link
                to="/paystub-generator/canada"
                className="px-3.5 py-1.5 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
              >
                Canada
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section ref={ctaRef} className="max-w-[1288px] mx-auto px-6 pb-20 md:pb-28">
        <div className={`rounded-[28px] bg-slate-100 px-6 py-14 md:py-20 text-center ${fade(ctaInView)}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Your {info.name} paystub, <span className="font-black">done</span>
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Accurate {isCanada ? "Canadian" : info.name} withholdings, professional templates, instant download.
          </p>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={() => navigate(ctaPath)}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </>
  );
}
